import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X } from 'lucide-react'

interface CheckinBodyPeekModalProps {
    open: boolean
    /** Header line — the checkin's date, or the diff's date header for a proposal. */
    title?: React.ReactNode
    onClose: () => void
    /** The body in full: the whole text for a card, the whole diff for a proposal. */
    children: React.ReactNode
}

/**
 * Read one clipped checkin body in full without leaving the conversation.
 *
 * Read-only, like `CollectionItemPeekModal` — same framing (dark scrim, header
 * with a close button, scrolling content) — and dismissed the way every other
 * modal here is: Escape, the backdrop, or the close button. Whose body it shows
 * is the list's business, not this component's: the surrounding list holds a
 * single open id, which is what keeps two of these from ever being open at once.
 */
const CheckinBodyPeekModal: React.FC<CheckinBodyPeekModalProps> = ({
    open,
    title,
    onClose,
    children,
}) => {
    React.useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] p-4"
                    // Only a click that both started and ended on the backdrop
                    // dismisses — a text-selection drag out of the card must not.
                    onClick={(e) => {
                        if (e.target === e.currentTarget) onClose()
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                >
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        className="bg-white rounded-lg shadow-xl flex flex-col w-[640px] max-w-[95vw] max-h-[90vh] overflow-hidden"
                        initial={{ opacity: 0, scale: 0.9, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 4 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                    >
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-sm font-medium text-gray-900 truncate pr-4">
                                {title}
                            </h3>
                            <motion.button
                                type="button"
                                onClick={onClose}
                                whileHover={{ scale: 1.1, rotate: 90 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                className="text-gray-400 hover:text-gray-600 shrink-0"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </motion.button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 py-4">
                            {children}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default CheckinBodyPeekModal
